import { lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router";
import { AppShell } from "./components/layout/AppShell";
import { ThemeProvider } from "./components/layout/ThemeProvider";
import { RequireAuth } from "./components/RequireAuth";
import { RequirePageAccess } from "./components/RequirePageAccess";
import { useAllowedPages, useAssignedRoleIds, useUserRole } from "./hooks/use-rbac";
import { isPathAllowed } from "@shared/rbac";
import { ALIAS_REDIRECTS } from "@shared/sidebar-pages";
import { salesHomeLanding } from "@shared/home-landing";
import { stationLanding } from "./lib/station-home";
import { ChunkLoadErrorBoundary } from "./components/ChunkLoadErrorBoundary";
import { DashboardSkeleton } from "./components/dashboard/DashboardSkeleton";
import { Login } from "./pages/Login";
import { Landing } from "./pages/Landing";
import { AuthReturn } from "./pages/AuthReturn";

// Lazy-loaded pages — each becomes its own chunk
const Overview = lazy(() => import("./pages/Overview"));
const Production = lazy(() => import("./pages/Production"));
// /quality renders the merged QCTestsHub (Latest Tests + Control Charts + QC
// Stats + Quality Scorecard as tabs); the four tab bodies are lazy chunks of
// the hub, so they are no longer imported here.
const QCTestsHub = lazy(() => import("./pages/QCTestsHub"));
// #1587: Latest Tests moved back OUT of the hub — "Recent Results" in Gus's
// menu, its own route again.
const LatestTests = lazy(() => import("./pages/LatestTests"));
// #714: /materials renders the merged MaterialsHub (Materials + Yield & Waste +
// Purchase Orders + Inventory as tabs); the four tab bodies are lazy chunks of the hub.
const MaterialsHub = lazy(() => import("./pages/MaterialsHub"));
// #1250: Material Sampling hub (Quality) — flows / lot coverage / sampling-vs-rule.
const MaterialSamplingHub = lazy(() => import("./pages/MaterialSamplingHub"));
const LabTesting = lazy(() => import("./pages/LabTesting"));
const Insights = lazy(() => import("./pages/Insights"));
const Guide = lazy(() => import("./pages/Guide"));
const Pending = lazy(() => import("./pages/Pending")); // #360: safe landing for unassigned users
const Explorer = lazy(() => import("./pages/Explorer"));
const QAEntry = lazy(() => import("./pages/QAEntry"));
const UPIManagement = lazy(() => import("./pages/UPIManagement"));
const SpecLimitsEditor = lazy(() => import("./pages/SpecLimitsEditor").then((m) => ({ default: m.SpecLimitsEditor })));
const ComplianceClauses = lazy(() => import("./pages/ComplianceClauses")); // #2458 read-only clause register
const QcVsTds = lazy(() => import("./pages/commercial/QcVsTds")); // #2545 QCTDS
const MixDesigns = lazy(() => import("./pages/MixDesigns"));
const Reminders = lazy(() => import("./pages/Reminders"));
const TestMonitor = lazy(() => import("./pages/TestMonitor"));
const Formulations = lazy(() => import("./pages/Formulations"));
const WeighOut = lazy(() => import("./pages/WeighOut"));
const RdtiTimesheet = lazy(() => import("./pages/RdtiTimesheet"));
// #717: /raw-materials renders the merged RawMaterialQCHub (Raw Materials
// Analytics + Supplier Quality as tabs); the two tab bodies are lazy chunks of the hub.
const RawMaterialQCHub = lazy(() => import("./pages/RawMaterialQCHub"));
const RmQcFieldManager = lazy(() => import("./pages/RmQcFieldManager"));
const RmMaterialPages = lazy(() => import("./pages/RmMaterialPages"));
const RmMaterialView = lazy(() => import("./pages/RmMaterialView"));
const RmCategoryGrid = lazy(() => import("./pages/RmCategoryGrid"));
const Certificates = lazy(() => import("./pages/Certificates"));
const DataAnalyst = lazy(() => import("./pages/DataAnalyst"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Artifacts = lazy(() => import("./pages/Artifacts"));
const Digests = lazy(() => import("./pages/Digests"));
const MonthlySummary = lazy(() => import("./pages/MonthlySummary"));
const SchedulePage = lazy(() => import("./pages/Schedule"));
const RmPlannerPage = lazy(() => import("./pages/RmPlanner"));
const ProductInventoryPage = lazy(() => import("./pages/ProductInventory"));
const Updates = lazy(() => import("./pages/Updates"));
const SharedConversation = lazy(() => import("./pages/SharedConversation"));
const Settings = lazy(() => import("./pages/Settings"));
const CostAnalysis = lazy(() => import("./pages/CostAnalysis"));
const RawMaterialPurchases = lazy(() => import("./pages/RawMaterialPurchases"));
const BomExplorer = lazy(() => import("./pages/BomExplorer"));
const BomUpdates = lazy(() => import("./pages/BomUpdates"));
const BomSummary = lazy(() => import("./pages/BomSummary"));
const BomMasterbatch = lazy(() => import("./pages/BomMasterbatch"));
const BomIntegrity = lazy(() => import("./pages/BomIntegrity"));
const BomInputs = lazy(() => import("./pages/BomInputs"));
const BomMap = lazy(() => import("./pages/BomMap"));
const BomSuiteShell = lazy(() => import("./pages/bom/BomSuiteShell"));
const AuthError = lazy(() => import("./pages/AuthError"));
// #2005: the public home-screen steps page (static, no session or API call).
const Install = lazy(() => import("./pages/Install"));
// #4447: the QC station PIN keypad (public: the station cookie, not a session, admits it).
const Station = lazy(() => import("./pages/Station"));
const Portal = lazy(() => import("./pages/Portal"));
const PortalAdmin = lazy(() => import("./pages/PortalAdmin"));
const ExecutiveReport = lazy(() => import("./pages/ExecutiveReport"));
// #715: /shifts renders the merged ShiftsHub (Shift Analysis + Changeovers as
// tabs); the two tab bodies are lazy chunks of the hub.
const ShiftsHub = lazy(() => import("./pages/ShiftsHub"));
const MaterialNetwork = lazy(() => import("./pages/MaterialNetwork"));
const CockpitPage = lazy(() => import("./pages/CockpitPage"));
const PlantIntelligence = lazy(() => import("./pages/PlantIntelligence"));
const CarbonDashboard = lazy(() => import("./pages/CarbonDashboard"));
const CarbonExec = lazy(() => import("./pages/CarbonExec"));
const ProductFamilies = lazy(() => import("./pages/ProductFamilies"));
const FlushWaste = lazy(() => import("./pages/FlushWaste"));
const FormulationOptimizer = lazy(() => import("./pages/FormulationOptimizer"));
const DisplayMode = lazy(() => import("./pages/DisplayMode"));
const ProductionCalendar = lazy(() => import("./pages/ProductionCalendar"));
const MarketIntelligence = lazy(() => import("./pages/MarketIntelligence"));
const HealthScorecard = lazy(() => import("./pages/HealthScorecard"));
const Admin = lazy(() => import("./pages/Admin"));
const ProductionMonitorScoped = lazy(() => import("./pages/ProductionMonitorScoped"));
const AIFeedback = lazy(() => import("./pages/AIFeedback"));
const AIFeedbackConversation = lazy(() => import("./pages/AIFeedbackConversation"));
const MaterialMapping = lazy(() => import("./pages/MaterialMapping"));
const OstendoAccess = lazy(() => import("./pages/OstendoAccess"));
const CalibrationIntelligence = lazy(() => import("./pages/CalibrationIntelligence"));
const RDPipeline = lazy(() => import("./pages/RDPipeline"));
const TdsMaker = lazy(() => import("./pages/TdsMaker"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Projects = lazy(() => import("./pages/Projects"));
const DailyDeliveries = lazy(() =>
  import("./pages/commercial/DailyDeliveries").then((m) => ({ default: m.DailyDeliveries }))
);
const CustomerList = lazy(() => import("./pages/commercial/CustomerList"));
const AgedDebtors = lazy(() => import("./pages/commercial/AgedDebtors"));
// AGEDREMIND #1197
const AgedDebtorsReminders = lazy(() => import("./pages/commercial/AgedDebtorsReminders"));
const SalesPipeline = lazy(() =>
  import("./pages/commercial/SalesPipeline").then((m) => ({ default: m.SalesPipeline }))
);
// COMPPRICE #3571
// #593 TDSVIEW: issued data sheets, every signed-in role (a UNIVERSAL_PAGES entry).
const DataSheets = lazy(() => import("./pages/DataSheets").then((m) => ({ default: m.DataSheets })));
const CompetitorPrices = lazy(() =>
  import("./pages/commercial/CompetitorPrices").then((m) => ({ default: m.CompetitorPrices }))
);
const Troubleshooting = lazy(() =>
  import("./pages/commercial/Troubleshooting").then((m) => ({ default: m.Troubleshooting }))
);
// #2058 Cases — the board, one case, one account.
const Cases = lazy(() =>
  import("./pages/commercial/Cases").then((m) => ({ default: m.CasesPage }))
);
const CaseDetail = lazy(() =>
  import("./pages/commercial/CaseDetail").then((m) => ({ default: m.CaseDetail }))
);
const CaseAccountDetail = lazy(() =>
  import("./pages/commercial/AccountDetail").then((m) => ({ default: m.CaseAccountDetail }))
);
// #2059 — the phone visit form; static path, registered before /commercial/cases/:id.
const VisitForm = lazy(() =>
  import("./pages/commercial/VisitForm").then((m) => ({ default: m.VisitForm }))
);

function PageFallback() {
  return <DashboardSkeleton cards={4} chart table />;
}

function MinimalFallback() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

/**
 * Landing route — phones go straight to Crosbot chat (Akira's primary surface on mobile,
 * per GH #105). Desktop + tablet (>= 640px viewport) keep the Overview dashboard.
 *
 * Breakpoint matches Tailwind `sm` so iPad portrait (768px) still gets the dashboard
 * — matches Akira's "desktop/tablet experience is unchanged" criterion.
 *
 * Synchronous Navigate (not useEffect-based redirect) so phone users don't see an
 * Overview flash before bouncing to chat. SSR-safe via `typeof window` check.
 */
export function HomeRoute() {
  const isPhone = typeof window !== "undefined" && window.innerWidth < 640;
  const pages = useAllowedPages();
  const roleIds = useAssignedRoleIds();
  const { data: freshRole, isError: rolesFailed } = useUserRole();

  // Role not yet known on a cold load (no cached pages) → brief loader rather
  // than flashing the Overview to a role that can't actually see it. Warm loads
  // read allowed_pages from localStorage instantly, so this only affects the
  // first visit of a session.
  if (pages === null || roleIds === null) return <PageFallback />;

  // #4449: a testing-station PC opens on its station view. It is a setting of
  // this device, so it beats the role default; phones and unknown pages skip it.
  // The cached pages can belong to a previous person on this browser, so the
  // redirect reads FRESH permissions only (same rule as AppShell's #1568 guard).
  // While the fresh read is pending, a device with a saved station waits on the
  // loader; if the read fails it lands as today, never on the station.
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  const freshPages = freshRole?.allowed_pages ?? null;
  if (freshPages === null && !rolesFailed && stationLanding(pages, width)) return <PageFallback />;
  const stationDest = stationLanding(freshPages, width);
  if (stationDest) return <Navigate to={stationDest} replace />;

  // Sales starts on the page built for its daily workflow on every viewport.
  // #4733: the dark sweep leaves `/` out for the same roles, by the same function.
  const salesHome = salesHomeLanding(roleIds, pages);
  if (salesHome) {
    return <Navigate to={salesHome} replace />;
  }

  // Role can't see the Overview ("/") → land on its first allowed page instead
  // of an Overview whose cards (costs/lab/etc.) would 403. e.g. qc_dashboards
  // has neither "/" nor /analyst, so it lands on its first QC page (a retired
  // path such as /quality-scorecard still resolves — the alias route redirects).
  if (!isPathAllowed("/", pages)) {
    const firstPage = pages.find((p) => p !== "/" && p !== "*");
    const dest = isPhone && isPathAllowed("/analyst", pages) ? "/analyst" : (firstPage ?? "/login");
    return <Navigate to={dest} replace />;
  }

  if (isPhone) return <Navigate to="/analyst" replace />;
  return <Overview />;
}

/**
 * The standalone Compare Mix Designs page is retired — it's now the "Compare"
 * tab on Mix Designs. This redirects old links (`?ids=1,2,3`) to their
 * equivalent tab deep link instead of 404ing.
 */
function MixDesignCompareRedirect() {
  const [searchParams] = useSearchParams();
  const ids = searchParams.get("ids");
  const target = `/rd/mix-designs?tab=compare${ids ? `&ids=${encodeURIComponent(ids)}` : ""}`;
  return <Navigate to={target} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system">
        <BrowserRouter>
          <ChunkLoadErrorBoundary>
          <Routes>
            <Route path="/welcome" element={<Landing />} />
            {/* Sign-in failures land here (better-auth onAPIError.errorURL) —
                outside the shell and outside RBAC: the user has no session. */}
            <Route path="/auth-error" element={<Suspense fallback={<PageFallback />}><AuthError /></Suspense>} />
            <Route path="/login" element={<Login />} />
            {/* #2005: public like /welcome and /login: outside RequireAuth, static content only. */}
            <Route path="/install" element={<Suspense fallback={<MinimalFallback />}><Install /></Suspense>} />
            {/* #4447: the QC station keypad, outside RequireAuth: nobody is signed in yet. */}
            <Route path="/station" element={<Suspense fallback={<MinimalFallback />}><Station /></Suspense>} />
            <Route path="/auth/return" element={<AuthReturn />} />
            <Route
              path="/portal/:token"
              element={
                <Suspense fallback={<MinimalFallback />}>
                  <Portal />
                </Suspense>
              }
            />
            <Route
              path="/display"
              element={
                <RequireAuth>
                  <RequirePageAccess path="/display">
                    <Suspense fallback={<MinimalFallback />}>
                      <DisplayMode />
                    </Suspense>
                  </RequirePageAccess>
                </RequireAuth>
              }
            />
            {/* #3503: a pure redirect sits outside AppShell so it resolves before
                the shell's route guard. Inside, the guard judged the bare
                /commercial itself, which no catalog grant opens, so a role holding
                /commercial/deliveries was sent to its first page instead. The
                target is still guarded (and signed-in checked) when it renders. */}
            <Route path="/commercial" element={<Navigate to="/commercial/deliveries" replace />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              {/* #719: retired paths (renames / Page-merges) redirect to their live
                  page instead of 404ing — driven by the catalog's alias declarations,
                  so adding an alias in sidebar-pages.ts is the whole client wiring.
                  #714: the redirect target comes from ALIAS_REDIRECTS, which carries
                  `?tab=` when the catalog declares an aliasTabs key — so a retired
                  path lands on ITS tab of the merged page (RBAC still resolves via
                  the pure-path PATH_ALIASES). Sub-routes are preserved by the splat
                  + canonicalPath semantics in rbac.ts; here a plain replace-redirect
                  is enough because no aliased page has sub-routes yet. */}
              {Object.entries(ALIAS_REDIRECTS).map(([oldPath, target]) => (
                <Route key={oldPath} path={oldPath} element={<Navigate to={target} replace />} />
              ))}
              <Route path="/" element={<Suspense fallback={<PageFallback />}><HomeRoute /></Suspense>} />
              <Route path="/production" element={<Suspense fallback={<PageFallback />}><Production /></Suspense>} />
              <Route path="/quality" element={<Suspense fallback={<PageFallback />}><QCTestsHub /></Suspense>} />
              <Route path="/latest-tests" element={<Suspense fallback={<PageFallback />}><LatestTests /></Suspense>} />
              <Route path="/materials" element={<Suspense fallback={<PageFallback />}><MaterialsHub /></Suspense>} />
              <Route path="/lab" element={<Suspense fallback={<PageFallback />}><LabTesting /></Suspense>} />
              <Route path="/insights" element={<Suspense fallback={<PageFallback />}><Insights /></Suspense>} />
              <Route path="/qa-entry" element={<Suspense fallback={<PageFallback />}><QAEntry /></Suspense>} />
              <Route path="/qa-upi" element={<Suspense fallback={<PageFallback />}><UPIManagement /></Suspense>} />
              <Route path="/spec-limits" element={<Suspense fallback={<PageFallback />}><SpecLimitsEditor /></Suspense>} />
              <Route path="/compliance/clauses" element={<Suspense fallback={<PageFallback />}><ComplianceClauses /></Suspense>} />
              {/* #2545: this route and its nav row open on allowed_pages, like every
                  page; the data behind it (/api/qc-vs-tds) also needs the
                  view_qc_vs_tds write action (server/index.ts). No page or nav row
                  on main gates on a write action, so a role given the page without
                  the action sees the row and a 403. Accepted: the held grant file
                  gives tech_services both, and the role gate before it split the
                  same way. */}
              <Route path="/commercial/qc-vs-tds" element={<Suspense fallback={<PageFallback />}><QcVsTds /></Suspense>} />
              <Route path="/reminders" element={<Suspense fallback={<PageFallback />}><Reminders /></Suspense>} />
              <Route path="/test-monitor" element={<Suspense fallback={<PageFallback />}><TestMonitor /></Suspense>} />
              <Route path="/formulations" element={<Suspense fallback={<PageFallback />}><Formulations /></Suspense>} />
              <Route path="/weigh-out" element={<Suspense fallback={<PageFallback />}><WeighOut /></Suspense>} />
              <Route path="/rdti" element={<Suspense fallback={<PageFallback />}><RdtiTimesheet /></Suspense>} />
              <Route path="/costs" element={<Suspense fallback={<PageFallback />}><CostAnalysis /></Suspense>} />
              <Route path="/costs/rm-purchases-fy26" element={<Suspense fallback={<PageFallback />}><RawMaterialPurchases /></Suspense>} />
              <Route element={<Suspense fallback={<PageFallback />}><BomSuiteShell /></Suspense>}>
                <Route path="/bom-explorer" element={<Suspense fallback={<PageFallback />}><BomExplorer /></Suspense>} />
                <Route path="/bom-updates" element={<Suspense fallback={<PageFallback />}><BomUpdates /></Suspense>} />
                <Route path="/bom-summary" element={<Suspense fallback={<PageFallback />}><BomSummary /></Suspense>} />
                <Route path="/bom-masterbatch" element={<Suspense fallback={<PageFallback />}><BomMasterbatch /></Suspense>} />
                <Route path="/bom-inputs" element={<Suspense fallback={<PageFallback />}><BomInputs /></Suspense>} />
                <Route path="/bom-map" element={<Suspense fallback={<PageFallback />}><BomMap /></Suspense>} />
                <Route path="/bom-integrity" element={<Suspense fallback={<PageFallback />}><BomIntegrity /></Suspense>} />
              </Route>
              <Route path="/shifts" element={<Suspense fallback={<PageFallback />}><ShiftsHub /></Suspense>} />
              <Route path="/material-network" element={<Suspense fallback={<PageFallback />}><MaterialNetwork /></Suspense>} />
              <Route path="/cockpit" element={<Suspense fallback={<PageFallback />}><CockpitPage /></Suspense>} />
              <Route path="/product-families" element={<Suspense fallback={<PageFallback />}><ProductFamilies /></Suspense>} />
              <Route path="/flush-waste" element={<Suspense fallback={<PageFallback />}><FlushWaste /></Suspense>} />
              <Route path="/calendar" element={<Suspense fallback={<PageFallback />}><ProductionCalendar /></Suspense>} />
              <Route path="/market" element={<Suspense fallback={<PageFallback />}><MarketIntelligence /></Suspense>} />
              <Route path="/commercial/deliveries" element={<Suspense fallback={<PageFallback />}><DailyDeliveries /></Suspense>} />
              <Route path="/commercial/pipeline" element={<Suspense fallback={<PageFallback />}><SalesPipeline /></Suspense>} />
              <Route path="/commercial/competitor-prices" element={<Suspense fallback={<PageFallback />}><CompetitorPrices /></Suspense>} />
              <Route path="/data-sheets" element={<Suspense fallback={<PageFallback />}><DataSheets /></Suspense>} />
              <Route path="/commercial/customers" element={<Suspense fallback={<PageFallback />}><CustomerList /></Suspense>} />
              <Route path="/commercial/aged-debtors" element={<Suspense fallback={<PageFallback />}><AgedDebtors /></Suspense>} />
              <Route path="/commercial/aged-debtors-reminders" element={<Suspense fallback={<PageFallback />}><AgedDebtorsReminders /></Suspense>} />
              <Route path="/commercial/troubleshooting" element={<Suspense fallback={<PageFallback />}><Troubleshooting /></Suspense>} />
              <Route path="/commercial/cases" element={<Suspense fallback={<PageFallback />}><Cases /></Suspense>} />
              <Route path="/commercial/cases/accounts/:id" element={<Suspense fallback={<PageFallback />}><CaseAccountDetail /></Suspense>} />
              <Route path="/commercial/cases/visits/new" element={<Suspense fallback={<PageFallback />}><VisitForm /></Suspense>} />
              <Route path="/commercial/cases/:id" element={<Suspense fallback={<PageFallback />}><CaseDetail /></Suspense>} />
              <Route path="/optimizer" element={<Suspense fallback={<PageFallback />}><FormulationOptimizer /></Suspense>} />
              <Route path="/intelligence" element={<Suspense fallback={<PageFallback />}><PlantIntelligence /></Suspense>} />
              <Route path="/health" element={<Suspense fallback={<PageFallback />}><HealthScorecard /></Suspense>} />
              <Route path="/carbon" element={<Suspense fallback={<PageFallback />}><CarbonDashboard /></Suspense>} />
              <Route path="/carbon-exec" element={<Suspense fallback={<PageFallback />}><CarbonExec /></Suspense>} />
              <Route path="/raw-materials" element={<Suspense fallback={<PageFallback />}><RawMaterialQCHub /></Suspense>} />
              <Route path="/raw-materials/field-manager" element={<Suspense fallback={<PageFallback />}><RmQcFieldManager /></Suspense>} />
              <Route path="/rm-materials" element={<Suspense fallback={<PageFallback />}><RmMaterialPages /></Suspense>} />
              <Route path="/rm-materials/:code" element={<Suspense fallback={<PageFallback />}><RmMaterialView /></Suspense>} />
              <Route path="/rm-categories" element={<Suspense fallback={<PageFallback />}><RmCategoryGrid /></Suspense>} />
              <Route path="/material-sampling" element={<Suspense fallback={<PageFallback />}><MaterialSamplingHub /></Suspense>} />
              <Route path="/certificates" element={<Suspense fallback={<PageFallback />}><Certificates /></Suspense>} />
              <Route path="/calibration" element={<Suspense fallback={<PageFallback />}><CalibrationIntelligence /></Suspense>} />
              <Route path="/rd/mix-designs" element={<Suspense fallback={<PageFallback />}><MixDesigns /></Suspense>} />
              <Route path="/rd/mix-designs/compare" element={<MixDesignCompareRedirect />} />
              <Route path="/rd/pipeline" element={<Suspense fallback={<PageFallback />}><RDPipeline /></Suspense>} />
              <Route path="/tds" element={<Suspense fallback={<PageFallback />}><TdsMaker /></Suspense>} />
              <Route path="/projects" element={<Suspense fallback={<PageFallback />}><Projects /></Suspense>} />
              <Route path="/projects/:id" element={<Suspense fallback={<PageFallback />}><ProjectDetail /></Suspense>} />
              <Route path="/guide" element={<Suspense fallback={<PageFallback />}><Guide /></Suspense>} />
              <Route path="/explore" element={<Suspense fallback={<PageFallback />}><Explorer /></Suspense>} />
              <Route path="/analyst" element={<Suspense fallback={<PageFallback />}><DataAnalyst /></Suspense>} />
              <Route path="/kb" element={<Suspense fallback={<PageFallback />}><KnowledgeBase /></Suspense>} />
              <Route path="/artifacts" element={<Suspense fallback={<PageFallback />}><Artifacts /></Suspense>} />
              <Route path="/artifacts/:id" element={<Suspense fallback={<PageFallback />}><Artifacts /></Suspense>} />
              <Route path="/digests" element={<Suspense fallback={<PageFallback />}><Digests /></Suspense>} />
              <Route path="/monthly-summary" element={<Suspense fallback={<PageFallback />}><MonthlySummary /></Suspense>} />
              <Route path="/schedule" element={<Suspense fallback={<PageFallback />}><SchedulePage /></Suspense>} />
              <Route path="/rm-planner" element={<Suspense fallback={<PageFallback />}><RmPlannerPage /></Suspense>} />
              <Route path="/product-inventory" element={<Suspense fallback={<PageFallback />}><ProductInventoryPage /></Suspense>} />
              <Route path="/updates" element={<Suspense fallback={<PageFallback />}><Updates /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<PageFallback />}><Settings /></Suspense>} />
              <Route path="/pending" element={<Suspense fallback={<PageFallback />}><Pending /></Suspense>} />
              <Route path="/portal-admin" element={<Suspense fallback={<PageFallback />}><PortalAdmin /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<PageFallback />}><Admin /></Suspense>} />
              {/* #1207: the two Production Monitor views are route-backed so the
                  unattended wallboard has its own bootable URL. Both are
                  accessAliases of the /production-monitor Page (one RBAC unit). */}
              <Route path="/production-monitor" element={<Suspense fallback={<PageFallback />}><ProductionMonitorScoped /></Suspense>} />
              <Route path="/production-monitor/live" element={<Suspense fallback={<PageFallback />}><ProductionMonitorScoped /></Suspense>} />
              <Route path="/admin/ai-feedback" element={<Suspense fallback={<PageFallback />}><AIFeedback /></Suspense>} />
              {/* #3286: read-only view of one flagged conversation, any owner. Under
                  /admin/ai-feedback so it inherits that page's grant; path is
                  AI_FEEDBACK_CONVERSATION_ROUTE (src/shared/ai-feedback-routes.ts). */}
              <Route path="/admin/ai-feedback/conversations/:id" element={<Suspense fallback={<PageFallback />}><AIFeedbackConversation /></Suspense>} />
              <Route path="/admin/material-mapping" element={<Suspense fallback={<PageFallback />}><MaterialMapping /></Suspense>} />
              <Route path="/admin/ostendo-access" element={<Suspense fallback={<PageFallback />}><OstendoAccess /></Suspense>} />
              <Route path="/report" element={<Suspense fallback={<PageFallback />}><ExecutiveReport /></Suspense>} />
              <Route path="/shared/:token" element={<Suspense fallback={<PageFallback />}><SharedConversation /></Suspense>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </ChunkLoadErrorBoundary>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
